import React from 'react';
import { Link } from 'react-router-dom';

const TermsOfService: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden py-24">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23EF5000%22%20fill-opacity%3D%220.05%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>
        
        <div className="relative px-4 mx-auto sm:max-w-xl md:max-w-full lg:max-w-screen-xl md:px-24 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="mb-6 text-5xl font-bold leading-tight text-white sm:text-6xl">
              Terms of <span className="bg-gradient-to-r from-primary-400 to-orange-400 bg-clip-text text-transparent">Service</span>
            </h1>
            <p className="max-w-2xl mx-auto mb-10 text-xl text-gray-300 leading-relaxed">
              Please read these terms carefully before using our URL shortening service.
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative py-24 bg-gray-800/30 backdrop-blur-sm">
        <div className="px-4 mx-auto sm:max-w-xl md:max-w-full lg:max-w-screen-xl md:px-24 lg:px-8">
          <div className="max-w-4xl mx-auto">
            
            {/* Last Updated */}
            <div className="mb-12 text-center">
              <p className="text-gray-400 text-sm">
                Last updated: October 3, 2025
              </p>
            </div>

            <div className="space-y-12">
              
              {/* Agreement */}
              <section className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Agreement to Terms</h2>
                <div className="text-gray-300 space-y-4">
                  <p>
                    These Terms of Service ("Terms") constitute a legally binding agreement between you and 
                    RF Digital ("Company," "we," "us," or "our") regarding your use of the Eddystone URL 
                    Shortener service (the "Service").
                  </p>
                  <p>
                    By accessing or using our Service, you agree to be bound by these Terms and our Privacy Policy. 
                    If you do not agree to these Terms, you may not access or use the Service.
                  </p>
                  <p>
                    These Terms are governed by the laws of South Africa, and any disputes will be subject to 
                    the jurisdiction of South African courts.
                  </p>
                </div>
              </section>

              {/* Service Description */}
              <section className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Service Description</h2>
                <div className="text-gray-300 space-y-4">
                  <p>
                    The Eddystone URL Shortener is a web-based service that allows users to create shortened 
                    versions of long URLs, with special optimization for Eddystone beacon technology. Our Service includes:
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>URL shortening and custom code creation</li>
                    <li>Click analytics and tracking</li>
                    <li>Eddystone beacon optimization</li>
                    <li>API access for developers</li>
                    <li>Account management and billing</li>
                  </ul>
                </div>
              </section>

              {/* User Accounts */}
              <section className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">User Accounts and Registration</h2>
                <div className="text-gray-300 space-y-4">
                  <p>
                    To access certain features of the Service, you must create an account. You agree to:
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Provide accurate, current, and complete information during registration</li>
                    <li>Maintain and update your account information</li>
                    <li>Keep your account credentials secure and confidential</li>
                    <li>Accept responsibility for all activities under your account</li>
                    <li>Notify us immediately of any unauthorized use of your account</li>
                  </ul>
                  <p>
                    You must be at least 18 years old or have parental consent to use the Service in accordance 
                    with South African law.
                  </p>
                </div>
              </section>

              {/* Acceptable Use */}
              <section className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Acceptable Use Policy</h2>
                <div className="text-gray-300 space-y-4">
                  <p>You agree not to use the Service to:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Violate any applicable laws or regulations</li>
                    <li>Infringe on intellectual property rights</li>
                    <li>Distribute malware, viruses, or harmful content</li>
                    <li>Engage in phishing, spam, or fraudulent activities</li>
                    <li>Link to illegal, offensive, or inappropriate content</li>
                    <li>Circumvent security measures or access restrictions</li>
                    <li>Interfere with the Service's operation or other users' access</li>
                    <li>Use the Service for commercial purposes without proper authorization</li>
                  </ul>
                  <p>
                    We reserve the right to suspend or terminate accounts that violate this policy.
                  </p>
                </div>
              </section>

              {/* Subscription and Billing */}
              <section className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Subscription and Billing</h2>
                <div className="text-gray-300 space-y-4">
                  <p>
                    Our Service offers both free and paid subscription plans. For paid plans:
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Fees are charged in South African Rand (ZAR) unless otherwise specified</li>
                    <li>Subscriptions automatically renew unless cancelled</li>
                    <li>All fees are non-refundable except as required by South African consumer protection laws</li>
                    <li>We may change pricing with 30 days' notice</li>
                    <li>You may cancel your subscription at any time through your account settings</li>
                  </ul>
                  <p>
                    Failure to pay fees may result in service suspension or termination.
                  </p>
                </div>
              </section>

              {/* Intellectual Property */}
              <section className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Intellectual Property Rights</h2>
                <div className="text-gray-300 space-y-4">
                  <p>
                    The Service and its original content, features, and functionality are owned by RF Digital 
                    and are protected by South African and international copyright, trademark, and other 
                    intellectual property laws.
                  </p>
                  <p>
                    You retain ownership of the URLs and content you submit to the Service. By using the Service, 
                    you grant us a limited license to process and display your content as necessary to provide the Service.
                  </p>
                </div>
              </section>

              {/* Data and Privacy */}
              <section className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Data and Privacy</h2>
                <div className="text-gray-300 space-y-4">
                  <p>
                    Your privacy is important to us. Our collection and use of personal information is governed 
                    by our Privacy Policy, which complies with the Protection of Personal Information Act (POPIA) 
                    of South Africa.
                  </p>
                  <p>
                    By using the Service, you consent to the collection and use of information as described in 
                    our Privacy Policy.
                  </p>
                </div>
              </section>

              {/* Service Availability */}
              <section className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Service Availability and Modifications</h2>
                <div className="text-gray-300 space-y-4">
                  <p>
                    We strive to provide reliable service but cannot guarantee 100% uptime. We reserve the right to:
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Modify, suspend, or discontinue the Service at any time</li>
                    <li>Perform maintenance and updates</li>
                    <li>Change features and functionality</li>
                    <li>Update these Terms with reasonable notice</li>
                  </ul>
                </div>
              </section>

              {/* Disclaimers */}
              <section className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Disclaimers and Limitation of Liability</h2>
                <div className="text-gray-300 space-y-4">
                  <p>
                    The Service is provided "as is" and "as available" without warranties of any kind. 
                    To the fullest extent permitted by South African law, we disclaim all warranties, 
                    express or implied.
                  </p>
                  <p>
                    We shall not be liable for any indirect, incidental, special, consequential, or punitive 
                    damages arising from your use of the Service, except as required by applicable consumer 
                    protection laws.
                  </p>
                </div>
              </section>

              {/* Termination */}
              <section className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Termination</h2>
                <div className="text-gray-300 space-y-4">
                  <p>
                    Either party may terminate this agreement at any time. We may terminate or suspend your 
                    account immediately for violations of these Terms or for any other reason.
                  </p>
                  <p>
                    Upon termination, your right to use the Service ceases immediately, and we may delete 
                    your account and data in accordance with our data retention policies.
                  </p>
                </div>
              </section>

              {/* Governing Law */}
              <section className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Governing Law and Dispute Resolution</h2>
                <div className="text-gray-300 space-y-4">
                  <p>
                    These Terms are governed by and construed in accordance with the laws of South Africa. 
                    Any disputes arising from these Terms or the Service shall be subject to the exclusive 
                    jurisdiction of the South African courts.
                  </p>
                  <p>
                    We encourage users to contact us directly to resolve any disputes before pursuing legal action.
                  </p>
                </div>
              </section>

              {/* Contact Information */}
              <section className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Contact Information</h2>
                <div className="text-gray-300 space-y-4">
                  <p>
                    If you have any questions about these Terms of Service, please contact us:
                  </p>
                  <div className="bg-gray-900/50 border border-gray-600 rounded-macos p-4">
                    <p><strong>RF Digital</strong></p>
                    <p>Email: legal@rfdigi.co.za</p>
                    <p>Address: South Africa</p>
                  </div>
                </div>
              </section>

            </div>

            {/* Back to Home */}
            <div className="mt-16 text-center">
              <Link
                to="/"
                className="inline-flex items-center px-6 py-3 text-base font-medium text-white bg-gradient-to-r from-primary-500 to-orange-500 rounded-macos hover:from-primary-600 hover:to-orange-600 transition-all duration-300 shadow-macos"
              >
                Back to Home
              </Link>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;